
import { Component, Ref, Watch, Vue } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

// Components
import VSpinner from '@/components/VSpinner.vue';
import VSelect from '@/components/VSelect.vue';
import VBreadcrumbs from '@/components/VBreadcrumbs.vue';
import VPageHeader from '@/components/VPageHeader.vue';
import VFormField from '@/components/VFormField.vue';
import VTable from '@/components/VTable.vue';
import VTableRow from '@/components/VTableRow.vue';
import VTableCell from '@/components/VTableCell.vue';

import PMReportWrapper from '@/components/ProjectManagement/PMReportWrapper.vue';
import PMPlaceholder from '@/components/ProjectManagement/PMPlaceholder.vue';

// Interfaces
import { PMTabTypeEnum } from '@/ship/Enums/ProjectManagementEnums';
import { IPMChart } from '@/ship/Models/IProjectManagement';
import IDictionary from '@/ship/Models/IDictionary';
import Observer from '@/ship/Models/IObserver';

// Mock
import { defaultChart, defaultReportConsolidatedCharts } from '@/mock/PMMockData';

// Mixins
import PMMixin from '@/mixins/PMMixin';

// Utils
import ErrorService from '@/utils/ErrorService';
import { get } from 'lodash';

@Component({
    components: {
        ValidationObserver,
        ValidationProvider,
        VFormField,
        VSpinner,
        VSelect,
        VBreadcrumbs,
        VPageHeader,
        VTable,
        VTableRow,
        VTableCell,

        PMReportWrapper,
        PMPlaceholder,
    },
    beforeRouteEnter(to, from, next) {
        next((vm: Vue) => {
            const self = vm as PMReportConsolidated;
            if (from.name === 'projectManagement.reports') {
                self.fromReportsPage = true;
            }
        });
    },
})
export default class PMReportConsolidated extends PMMixin {
    @Ref() public observer!: Observer;
    @Ref() public lastSelect!: VSelect;

    chartSelects: number[] = [];
    chartSelectsCount = 0;

    lastChartId: number | null = null;

    templateData: IDictionary = {
        template: '',
        headers: [],
        columns: [],
        leftCount: NaN,
    };
    rows: IDictionary[] = [];

    isLoading = false;

    hasValidationAttempt = false;

    defaultModel: IDictionary[] = [];
    model: IDictionary[] = [];
    allCharts: IDictionary[] = [];

    reportConsolidated: IDictionary[] = [];

    chartOptions: IDictionary[] = [];

    chart: IPMChart | null = defaultChart;
    loading = false;

    readonly reduceHandler = (x: any) => x?.id;

    fromReportsPage = false;

    searchString = '';

    resetIsNeeded = false;

    readonly selectorBind = {
        label: 'name',
        multiple: true,
        trackBy: 'id',
        reduce: (x: unknown) => get(x, 'id'),
    };

    readonly leftHeaderCellsCount = 2;

    get isNotEmptyModel() {
        return !!this.model.length;
    }

    get leftHeaders() {
        return this.headers.slice(0, this.leftHeaderCellsCount);
    }

    get rightHeaders() {
        return this.headers.slice(this.leftHeaderCellsCount);
    }

    get headerRowTemplate() {
        const codeSdr = '6.125rem ';
        const name = '27.5rem ';

        const beginEnd = '7.75rem ';
        const beginEndBP = '8.625rem ';
        const percentReadiness = '5.75rem ';
        const chart = beginEnd + beginEndBP + percentReadiness;
        let charts = '';
        for (let i = 0; i < this.model.length; i++) {
            charts += chart;
        }
        const last = '';

        return codeSdr + name + charts + last;
    }

    get headers() {
        let headers = [this.$t(`${this.prefix}.column.codeSDR`), this.$t(`${this.prefix}.column.title`)];

        const chartHeaders = [
            this.$t(`${this.prefix}.column.beginning`),
            this.$t(`${this.prefix}.column.beginBPAt`),
            this.$t(`${this.prefix}.column.percentReady`),
        ];

        for (let i = 0; i < this.model.length; i++) {
            headers = headers.concat(chartHeaders);
        }

        return headers;
    }

    get topRowTemplate() {
        const title = '33.625rem ';
        const items = this.model.length ? `repeat(${this.model.length}, 22.125rem)` : '';
        const last = ' 22.125rem';
        return title + items + last;
    }

    get showChart() {
        return `${this.$t('show')} ${(this.$t('chart') as string).toLowerCase()}`;
    }
    get currentRoute() {
        return { ...this.$route, label: 'test' };
    }

    get chartType() {
        return this.$t(`${this.prefix}.chart.weeklyReport.title`);
    }

    get projectName() {
        return this.chart?.name;
    }

    get routes() {
        return this.fromReportsPage ? this.reportRoutes : this.chartRoutes;
    }

    get chartRoutes() {
        return [
            { name: `projectManagement.charts`, label: this.$t(`${this.prefix}.charts.title`) },
            {
                name: `projectManagement.chart.info`,
                params: { id: this.chart?.id.toString() },
                label: this.chart?.name,
            },
            {
                name: `projectManagement.chart.info`,
                params: { id: this.chart?.id.toString(), tab: PMTabTypeEnum.chartInfo },
                label: this.$t(`${this.prefix}.tab.chartInfo`),
            },
        ];
    }

    get reportRoutes() {
        return [{ name: `projectManagement.reports`, label: this.$t(`${this.prefix}.reports.title`) }];
    }

    get combinedRows() {
        let mainRows: IDictionary[] = [];
        if (this.model.length) {
            const firstChart = this.allCharts.find((el) => el.id === this.model[0].id)!;

            mainRows = firstChart.rows.map((chartRow) => ({
                codeSDR: chartRow.codeSDR,
                name: chartRow.name,
                data: [],
            }));

            this.model.forEach((modelEl) => {
                const foundChart = this.allCharts.find((chartEl) => chartEl.id === modelEl.id)!;

                foundChart.rows.forEach((chartRow) => {
                    const foundSdr = mainRows.find((mainRow) => mainRow.codeSDR === chartRow.codeSDR);

                    if (!foundSdr) {
                        mainRows.push({ codeSDR: chartRow.codeSDR, name: chartRow.name, data: [] });
                    }
                });
            });

            mainRows.forEach((mainRow) => {
                const mainCodeSDR = mainRow.codeSDR;

                this.model.forEach((modelEl) => {
                    const foundChart = this.allCharts.find((chartEl) => chartEl.id === modelEl.id)!;

                    const matchedRow = foundChart.rows.find((chartRow) => chartRow.codeSDR === mainCodeSDR);

                    let newData = ['-', '-', '-'];

                    if (matchedRow) {
                        newData = matchedRow.data;
                    }

                    mainRow.data = mainRow.data.concat(newData);
                });
            });
        } else {
            mainRows = [];
        }
        return mainRows;
    }

    async created() {
        this.loadData();
    }

    @Watch('lastChartId')
    lastChartIdWatchHandler() {
        if (this.lastChartId !== null) {
            const chartSelect = this.allCharts.find((chart) => chart.id === this.lastChartId)!;

            this.model.push({ id: chartSelect.id, ref: `ref-${chartSelect.id}` });

            this.resetIsNeeded = true;
        } else {
            this.resetIsNeeded = false;
        }
    }

    @Watch('resetIsNeeded')
    resetIsNeededWatchHandler() {
        if (this.resetIsNeeded) {
            this.lastSelect.clearSelected();
        }
    }

    leftCells(row: IDictionary) {
        const cells = [];
        cells.push(row.codeSDR);
        cells.push(row.name);

        return cells;
    }

    rightCells(row: IDictionary) {
        const cells = row.data;

        return cells;
    }

    selectClearedHandler() {
        this.model = this.model.filter((el) => el.id !== null);
    }

    loadData() {
        this.model = this.defaultModel;
        this.allCharts = this.getAllCharts();
        this.chartOptions = this.allCharts.map((el) => {
            return { name: el.name, id: el.id };
        });
    }

    getAllCharts() {
        return defaultReportConsolidatedCharts;
    }
}
